<script setup lang="ts">
  defineOptions({ name: 'DiscoverNavbarSearch' })

  import { ref, watch } from 'vue'

  import { useRoute, useRouter } from 'nuxt/app'

  import { SearchLgIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPInput from '@ui/components/CPInput.vue'

  import { useDiscoverSearchContext } from '../composables/useDiscoverSearchContext'

  const route = useRoute()
  const router = useRouter()

  /**
   * Fills the shared `TopBar` `#search` slot on `/discover`.
   *
   * Deliberately thin: it is the desktop face of the *same* `useDiscoverSearchContext` the shell
   * already hands to `TopBarMobile`, so `?q=` stays the single source of truth and there is no
   * second search state to keep in sync. Unlike the courses/communities widgets there is no result
   * dropdown — Discover filters in place.
   */
  const { initialQuery, onInputChange, onSubmit, onSearchClose, placeholder } = useDiscoverSearchContext(route, router)

  const searchInput = ref(initialQuery.value)

  const handleInput = (value: string) => {
    searchInput.value = value
    onInputChange?.(value)
  }

  /** Enter bypasses the context debounce. */
  const handleSubmit = () => {
    onSubmit(searchInput.value)
  }

  const handleClear = () => {
    searchInput.value = ''
    onSearchClose?.()
  }

  // Mirrors `?q=` back into the field on back/forward navigation or an external clear.
  watch(initialQuery, value => {
    if (value !== searchInput.value) {
      searchInput.value = value
    }
  })
</script>

<template>
  <CPInput
    :id="createElementId('discover', 'navbar-search', 'input')"
    class="w-full"
    type="text"
    size="sm"
    clearable
    :model-value="searchInput"
    :placeholder="placeholder"
    @update:model-value="handleInput"
    @keydown.enter.prevent="handleSubmit"
    @clear="handleClear"
  >
    <template #prefix>
      <SearchLgIcon :id="createElementId('discover', 'navbar-search', 'icon')" class="h-4 w-4 text-secondary" aria-hidden="true" />
    </template>
  </CPInput>
</template>
